import React, { useCallback, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDatabase } from "@fortawesome/free-solid-svg-icons";
import { useReactFlow } from "reactflow";
import {
  DatasetNodeSource,
  DATASET_FORMAT_LABEL,
  datasetProvenanceLabel,
  isNodeLinkedToAnyDataset,
} from "../../services/datasetCatalog";
import { useDatasetPalette } from "../../providers/DatasetPaletteContext";
import { useHeaderIconDragClick } from "../../utils/headerIconDragClick";
import { focusLinkedNodes } from "../../utils/focusDatasetNodes";
import { useNotebookViewContext } from "../../providers/flow/notebookViewContext";
import styles from "./DatasetMetaHeader.module.css";

export interface DatasetMetaHeaderProps {
  source: DatasetNodeSource;
  /** ``consumer`` - node loads this dataset (palette-created loader; pill reads
   * "DATASET"). ``producer`` - node generated this dataset (pill reads "OUTPUT"). */
  variant?: "consumer" | "producer";
  /** When the owning node is selected on the canvas, render a more vibrant pill. */
  selected?: boolean;
  suggestionActive?: boolean;
}

/**
 * Dataset linkage pill for the canvas node title bar. Clicking it reveals the
 * linked installed listing in the dataset palette. Independent of the PACKAGE
 * pill and of each other: a node may show consumer and producer pills at once.
 *  - ``consumer`` - rendered on nodes created from the dataset palette (``datasetSource``).
 *  - ``producer`` - rendered on nodes that generated an installed computed dataset.
 */
export function DatasetMetaHeader({
  source,
  variant = "consumer",
  selected = false,
  suggestionActive = false,
}: DatasetMetaHeaderProps) {
  const { setDatasetRevealId, installedComputedByProducer } = useDatasetPalette();
  const reactFlow = useReactFlow();
  const { reveal } = useNotebookViewContext();
  const isProducer = variant === "producer";
  const label = isProducer ? "OUTPUT" : "DATASET";
  const badgeClassName = `${styles.datasetBadge} ${selected ? styles.datasetBadgeSelected : ""}`;

  const tooltip = useMemo(() => {
    const lines = [source.title];
    const meta = [datasetProvenanceLabel(source.origin), DATASET_FORMAT_LABEL[source.format]]
      .filter(Boolean)
      .join(" · ");
    if (meta) lines.push(meta);
    lines.push(
      isProducer
        ? "Generated by this node · Click to reveal in the dataset palette"
        : "Click to reveal in the dataset palette",
    );
    return lines.join("\n");
  }, [source.title, source.origin, source.format, isProducer]);

  const revealInPalette = useCallback(() => {
    if (suggestionActive) return;
    // Highlight every node linked to the same dataset row (consumer via
    // datasetSource, producer via the catalog producer map), then reveal the row.
    const isLinked = (n: { id: string; data: any }) =>
      isNodeLinkedToAnyDataset(n.data, [source.datasetId]) ||
      installedComputedByProducer.get(n.id)?.id === source.datasetId;
    focusLinkedNodes(reactFlow, isLinked, reveal);
    setDatasetRevealId(source.datasetId);
  }, [source.datasetId, setDatasetRevealId, installedComputedByProducer, reactFlow, suggestionActive, reveal]);

  const badgeClick = useHeaderIconDragClick(revealInPalette);

  return (
    <div className={styles.pills} style={suggestionActive ? { pointerEvents: "none" } : undefined}>
      <button
        type="button"
        className={badgeClassName}
        data-tooltip={tooltip}
        aria-label={`Reveal ${isProducer ? "output" : "dataset"} ${source.title} in the dataset palette`}
        {...badgeClick}
      >
        <FontAwesomeIcon icon={faDatabase} className={styles.icon} aria-hidden />
        {label}
      </button>
    </div>
  );
}
